import { useEffect, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  Link,
  Modal,
  Typography,
} from '@mui/material'
import Bugsnag from '@bugsnag/js'
import { AxiosError } from 'axios'
import { MdAccessTime, MdCheck, MdClose, MdExpandMore, MdStar } from 'react-icons/md'
import { client } from '../../utils/client'
import { MarketplaceThemeType, ProductShowcaseType } from '../../utils/sharedTypes'
import { WALTER_COLORS } from '../../utils/artDirection'
import {
  formatBudgetLabel,
  formatDuration,
  formatPrice,
  formatRating,
  organismKey,
  productLogotypeBlackUrl,
} from './marketplaceAssets'

type ProductDetailModalPropsType = {
  theme: MarketplaceThemeType | null
  open: boolean
  inCart: boolean
  /** Formation déjà rattachée à une inscription du groupe : on n'en propose pas une seconde. */
  alreadyRegistered: boolean
  onClose: () => void
  onToggleCart: () => void
  /** Formation financée entièrement par le financeur imposé (IPERIA) : pas de prix à afficher. */
  hidePrice?: boolean
}

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
    {children}
  </Typography>
)

const ProductDetailModal = ({
  theme,
  open,
  inCart,
  alreadyRegistered,
  onClose,
  onToggleCart,
  hidePrice = false,
}: ProductDetailModalPropsType) => {
  const [showcase, setShowcase] = useState<ProductShowcaseType | null>(null)
  const [logotypeFailed, setLogotypeFailed] = useState(false)
  const product = theme?.default_product ?? null
  const productCode = product?.product_code

  // La fiche vitrine n'existe pas pour tous les produits (404 nominal côté S3) : la modale s'ouvre
  // immédiatement sur les données déjà en main et s'enrichit ensuite si le showcase répond.
  useEffect(() => {
    setShowcase(null)
    setLogotypeFailed(false)
    if (!open || !productCode) return

    let cancelled = false
    client
      .get(`/registration/content/products/${productCode}/showcase/`)
      .then((resp) => {
        if (!cancelled) setShowcase(resp.data as ProductShowcaseType)
      })
      .catch((err: AxiosError) => {
        if (err.response?.status !== 404) {
          Bugsnag.notify(err)
          console.error(err)
        }
      })

    return () => {
      cancelled = true
    }
  }, [open, productCode])

  if (!theme) return null

  const title = theme.title || theme.theme_code
  const price = hidePrice ? null : formatPrice(product?.listing_price)
  const duration = formatDuration(product?.duration)
  const rating = formatRating(
    showcase?.ratings?.avg ?? product?.ratings_avg,
    showcase?.ratings?.count ?? product?.ratings_count,
  )
  const subtitle = showcase?.subtitle || theme.selling_point || product?.selling_point
  const objectives = product?.objectives?.objectives ?? showcase?.objectives.objectives ?? []
  const keyStrengths = showcase?.key_strengths_formation ?? []
  const programParts = showcase?.program.parts ?? []
  const programUrl = showcase?.pdf_program_url || product?.program_url

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: { xs: 0, md: 3 } }}
    >
      {/* Colonne à hauteur bornée : en-tête et pied de page fixes, contenu scrollable au milieu.
          L'alternative (laisser l'overlay scroller) rendait le contenu inatteignable dès que
          l'iframe de la bande annonce interceptait la molette. */}
      <Box
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: { xs: 0, md: 3 },
          width: '100%',
          maxWidth: 860,
          maxHeight: { xs: '100%', md: '92vh' },
          height: { xs: '100%', md: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          outline: 'none',
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Grid container alignItems="flex-start" sx={{ p: 3, pb: 2 }} spacing={2} wrap="nowrap">
            <Grid item xs>
              {productCode && !logotypeFailed ? (
                <Box
                  component="img"
                  src={productLogotypeBlackUrl(productCode)}
                  alt={title}
                  onError={() => setLogotypeFailed(true)}
                  sx={{ height: 'auto', maxHeight: 44, maxWidth: '100%', mb: 1 }}
                />
              ) : (
                <Typography variant="h5">{title}</Typography>
              )}
              {subtitle && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {subtitle}
                </Typography>
              )}
            </Grid>
            <Grid item>
              <IconButton onClick={onClose} aria-label="Fermer">
                <MdClose />
              </IconButton>
            </Grid>
          </Grid>

          <Grid container spacing={1} sx={{ px: 3, pb: 2 }} alignItems="center">
            {duration && (
              <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                <MdAccessTime size={16} />
                <Typography variant="body2">{duration}</Typography>
              </Grid>
            )}
            {price && (
              <Grid item>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {price}
                </Typography>
              </Grid>
            )}
            {rating && (
              <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <MdStar size={16} />
                <Typography variant="body2">{rating.detailedLabel}</Typography>
              </Grid>
            )}
          </Grid>

          <Grid container spacing={0.75} sx={{ px: 3, pb: 2 }}>
            {theme.eligible_funding_organisms.map((organism) => {
              const budget = formatBudgetLabel(organism)
              return (
                <Grid item key={organismKey(organism)}>
                  <Chip
                    size="small"
                    label={budget ? `${organism.code || organism.name} · ${budget}` : organism.code || organism.name}
                  />
                </Grid>
              )
            })}
          </Grid>
          <Divider />
        </Box>

        <Box sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0 }}>
          {showcase?.teaser_embed_url && (
            <Box sx={{ px: 3, pt: 3 }}>
              <Box
                sx={{
                  position: 'relative',
                  pt: '56.25%',
                  borderRadius: 2,
                  overflow: 'hidden',
                  // Fond sombre plutôt que blanc le temps que le lecteur s'initialise.
                  backgroundColor: WALTER_COLORS.santeBlack,
                }}
              >
                <Box
                  component="iframe"
                  src={showcase.teaser_embed_url}
                  title={`Bande annonce ${title}`}
                  allow="accelerometer; gyroscope; encrypted-media; picture-in-picture; fullscreen"
                  allowFullScreen
                  sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                />
              </Box>
            </Box>
          )}

          <Box sx={{ p: 3 }}>
            {(showcase?.preamble || theme.description) && (
              <Box sx={{ mb: 3 }}>
                <SectionTitle>La formation</SectionTitle>
                <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-line' }}>
                  {showcase?.preamble || theme.description}
                </Typography>
              </Box>
            )}

            {objectives.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <SectionTitle>Les compétences visées</SectionTitle>
                <Grid container direction="column" spacing={0.75}>
                  {objectives.map((objective, index) => (
                    <Grid item key={objective.label || index} sx={{ display: 'flex', gap: 1 }}>
                      <Box sx={{ color: 'text.primary', display: 'flex', pt: '3px' }}>
                        <MdCheck size={16} />
                      </Box>
                      <Typography variant="body2">{objective.text || objective.label}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {keyStrengths.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <SectionTitle>Les points forts</SectionTitle>
                <Grid container spacing={0.75}>
                  {keyStrengths.map((strength) => (
                    <Grid item key={strength}>
                      <Chip size="small" label={strength} />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {programParts.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <SectionTitle>Le programme</SectionTitle>
                {programParts.map((part, index) => (
                  <Accordion key={part.title || index} disableGutters variant="outlined">
                    <AccordionSummary expandIcon={<MdExpandMore />}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {part.title}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container direction="column" spacing={0.5}>
                        {part.chapters.map((chapter, chapterIndex) => (
                          <Grid item key={chapter.title || chapterIndex}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {chapter.title}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}

            {showcase && showcase.teachers.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <SectionTitle>{showcase.teachers.length > 1 ? 'Vos formateurs' : 'Votre formateur'}</SectionTitle>
                <Grid container direction="column" spacing={2}>
                  {showcase.teachers.map((teacher) => (
                    <Grid
                      item
                      container
                      spacing={1.5}
                      wrap="nowrap"
                      key={`${teacher.user.first_name}-${teacher.user.last_name}`}
                    >
                      <Grid item>
                        <Avatar>{(teacher.user.first_name || '?').charAt(0).toUpperCase()}</Avatar>
                      </Grid>
                      <Grid item xs>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {`${teacher.user.first_name} ${teacher.user.last_name}`.trim()}
                        </Typography>
                        {teacher.title && (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {teacher.title}
                          </Typography>
                        )}
                        {teacher.description && (
                          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                            {teacher.description}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {(showcase?.public || showcase?.prerequisite) && (
              <Box sx={{ mb: 3 }}>
                <SectionTitle>Public et prérequis</SectionTitle>
                {showcase.public && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {showcase.public}
                  </Typography>
                )}
                {showcase.prerequisite && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    {showcase.prerequisite}
                  </Typography>
                )}
              </Box>
            )}

            {programUrl && (
              <Link href={programUrl} target="_blank" rel="noopener noreferrer" variant="body2">
                Consulter le programme détaillé
              </Link>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            backgroundColor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            p: 2,
            display: 'flex',
            gap: 1,
            justifyContent: 'flex-end',
          }}
        >
          <Button variant="text" onClick={onClose}>
            Fermer
          </Button>
          <Button
            variant={inCart ? 'outlined' : 'contained'}
            disabled={!product || alreadyRegistered}
            onClick={onToggleCart}
            startIcon={inCart || alreadyRegistered ? <MdCheck /> : undefined}
          >
            {alreadyRegistered ? 'Déjà dans vos inscriptions' : inCart ? 'Retirer du panier' : 'Ajouter à mon panier'}
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}

export default ProductDetailModal
